import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { CheckSquare, Square, ShoppingBag, Zap, ExternalLink, Clock, X, CalendarClock, Repeat } from "lucide-react";
import { formatMoney } from "@/lib/currency";

/**
 * Bulk-eBay cross-post queue — inventory items with photos + sale_price that
 * haven't been synced to eBay yet. One click cross-posts up to 50 items with
 * ~1.2s spacing between each to respect eBay rate limits. Continues on failure.
 */
export default function EbayCrossPostInbox() {
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [excluded, setExcluded] = useState(new Set());
  const [posting, setPosting] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [showCustomScheduler, setShowCustomScheduler] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const [customTime, setCustomTime] = useState("09:00");
  const [repeatWeekly, setRepeatWeekly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/inventory/ebay-cross-post-queue");
      setData(d);
      setExcluded(new Set());
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("ebay cross-post queue load failed", e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedIds = data.items.filter((i) => !excluded.has(i.id)).map((i) => i.id);

  const crossPost = async () => {
    if (!selectedIds.length) {
      toast.error("nothing selected to cross-post.");
      return;
    }
    const capped = selectedIds.slice(0, 50);
    if (!window.confirm(
      `Cross-post ${capped.length} item${capped.length === 1 ? "" : "s"} to eBay?\n\n` +
      `This will run sequentially with ~1.2s between items (approx ${Math.ceil(capped.length * 1.5)}s total).\n` +
      `Failures won't stop the batch — you'll see a per-item summary at the end.`
    )) return;
    setPosting(true);
    setLastRun(null);
    try {
      const { data: r } = await api.post("/inventory/bulk-cross-post-ebay", {
        item_ids: capped,
        max_items: 50,
      });
      setLastRun(r);
      if (r.failed === 0) {
        toast.success(`${r.successful} item${r.successful === 1 ? "" : "s"} cross-posted to eBay.`);
      } else if (r.successful === 0) {
        toast.error(`all ${r.failed} attempt${r.failed === 1 ? "" : "s"} failed. see details below.`);
      } else {
        toast.warning(`mixed result · ${r.successful} ✓ · ${r.failed} ✗ — see details below.`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "bulk cross-post failed.");
    } finally {
      setPosting(false);
    }
  };

  const schedule = async () => {
    if (!selectedIds.length) {
      toast.error("nothing selected to schedule.");
      return;
    }
    const capped = selectedIds.slice(0, 50);
    const repeatNote = repeatWeekly ? "\n\n🔁 This batch will also repeat every 7 days at the same time." : "";
    if (!window.confirm(
      `Schedule ${capped.length} item${capped.length === 1 ? "" : "s"} for tomorrow at 9:00 AM Eastern (buyer prime-time)?\n\n` +
      `The batch will run automatically — you don't need to be at the desk.\n` +
      `You can cancel any pending batch from the "scheduled batches" panel below.` + repeatNote
    )) return;
    setPosting(true);
    try {
      const { data: r } = await api.post("/inventory/schedule-cross-post-ebay", {
        item_ids: capped,
        max_items: 50,
        preset: "tomorrow_9am",
        repeat_weekly: repeatWeekly,
      });
      toast.success(r.message || "batch scheduled.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not schedule batch.");
    } finally {
      setPosting(false);
    }
  };

  const scheduleCustom = async () => {
    if (!selectedIds.length) {
      toast.error("nothing selected to schedule.");
      return;
    }
    if (!customDate || !customTime) {
      toast.error("pick a date and time first.");
      return;
    }
    // customDate: YYYY-MM-DD, customTime: HH:MM (both in the browser's local timezone).
    // Convert to UTC ISO for the backend.
    const localDate = new Date(`${customDate}T${customTime}:00`);
    if (isNaN(localDate.getTime())) {
      toast.error("invalid date/time.");
      return;
    }
    if (localDate.getTime() <= Date.now()) {
      toast.error("time must be in the future.");
      return;
    }
    const capped = selectedIds.slice(0, 50);
    const tzLabel = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const repeatNote = repeatWeekly ? " (repeats weekly)" : "";
    if (!window.confirm(
      `Schedule ${capped.length} item${capped.length === 1 ? "" : "s"} for ${localDate.toLocaleString()} (${tzLabel})${repeatNote}?`
    )) return;
    setPosting(true);
    try {
      const { data: r } = await api.post("/inventory/schedule-cross-post-ebay", {
        item_ids: capped,
        max_items: 50,
        run_at: localDate.toISOString(),
        repeat_weekly: repeatWeekly,
      });
      toast.success(r.message || "batch scheduled.");
      setShowCustomScheduler(false);
      setCustomDate("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not schedule batch.");
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return <p className="text-xs text-taupe">loading the cross-post queue…</p>;
  }

  return (
    <section data-testid="ebay-cross-post-inbox">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl lowercase text-charcoal flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-brass" /> eBay cross-post queue
          </h2>
          <p className="text-xs text-espresso mt-1">
            inventory with photos and a sale price that hasn&apos;t been synced to eBay yet. up to 50 items per batch.
          </p>
        </div>
        {data.count > 0 && (
          <div className="flex flex-wrap gap-2 items-end">
            <label
              className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-espresso cursor-pointer select-none pb-2.5"
              title="If enabled, this scheduled batch will auto-clone itself every 7 days."
              data-testid="repeat-weekly-label"
            >
              <input
                type="checkbox"
                checked={repeatWeekly}
                onChange={(e) => setRepeatWeekly(e.target.checked)}
                className="accent-brass"
                data-testid="repeat-weekly-toggle"
              />
              <Repeat className="w-3 h-3 text-brass" />
              repeat weekly
            </label>
            <button
              onClick={() => setShowCustomScheduler((v) => !v)}
              disabled={posting || !selectedIds.length}
              data-testid="toggle-custom-scheduler-btn"
              className="border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory px-3 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
              title="Schedule for a specific date and time"
            >
              <CalendarClock className="w-3 h-3" />
              custom time
            </button>
            <button
              onClick={schedule}
              disabled={posting || !selectedIds.length}
              data-testid="schedule-cross-post-btn"
              className="border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory px-4 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
              title="Schedule for tomorrow 9 AM Eastern (buyer prime-time)"
            >
              <Clock className="w-3 h-3" />
              schedule · tomorrow 9am et
            </button>
            <button
              onClick={crossPost}
              disabled={posting || !selectedIds.length}
              data-testid="bulk-cross-post-btn"
              className="bg-charcoal hover:bg-brass text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
            >
              <Zap className="w-3 h-3" />
              {posting ? "posting…" : `cross-post ${Math.min(selectedIds.length, 50)} → ebay`}
            </button>
          </div>
        )}
      </div>

      {showCustomScheduler && data.count > 0 && (
        <div className="mb-4 bg-linen border border-taupe/50 p-4 flex flex-wrap items-end gap-3" data-testid="custom-scheduler-panel">
          <div>
            <label className="block editorial-label mb-1" htmlFor="custom-sched-date">date</label>
            <input
              id="custom-sched-date"
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="border border-taupe px-3 py-2 text-sm bg-ivory"
              data-testid="custom-sched-date"
            />
          </div>
          <div>
            <label className="block editorial-label mb-1" htmlFor="custom-sched-time">time</label>
            <input
              id="custom-sched-time"
              type="time"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              className="border border-taupe px-3 py-2 text-sm bg-ivory"
              data-testid="custom-sched-time"
            />
          </div>
          <p className="text-[10px] text-espresso uppercase tracking-widest pb-2">
            local · {Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, " ")}
          </p>
          <button
            onClick={scheduleCustom}
            disabled={posting || !customDate || !customTime || !selectedIds.length}
            data-testid="schedule-custom-btn"
            className="bg-charcoal hover:bg-brass text-ivory px-4 py-2 text-xs tracking-[0.25em] uppercase disabled:opacity-50"
          >
            schedule
          </button>
          <button
            onClick={() => setShowCustomScheduler(false)}
            className="text-xs text-espresso hover:text-charcoal underline pb-2"
            data-testid="cancel-custom-scheduler-btn"
          >
            cancel
          </button>
        </div>
      )}

      {data.count === 0 ? (
        <div className="border border-dashed border-taupe/50 p-8 text-center bg-linen/50" data-testid="empty-cross-post-queue">
          <p className="font-serif text-xl lowercase text-taupe">no items awaiting cross-post.</p>
          <p className="text-xs text-espresso mt-2">
            items appear here as soon as they have at least one photo and a sale price set. connect eBay in{" "}
            <Link to="/admin/settings" className="text-brass underline">studio settings</Link>{" "}
            if the sync fails.
          </p>
        </div>
      ) : (
        <div className="border border-taupe/40 bg-ivory">
          <ul className="divide-y divide-taupe/30 max-h-[500px] overflow-y-auto" data-testid="cross-post-queue-list">
            {data.items.map((it) => {
              const isExcluded = excluded.has(it.id);
              return (
                <li
                  key={it.id}
                  className={`flex items-center gap-4 px-4 py-3 transition-colors ${isExcluded ? "opacity-50" : ""}`}
                  data-testid={`crosspost-item-${it.id.slice(0, 8)}`}
                >
                  <button
                    onClick={() => toggle(it.id)}
                    className="text-charcoal hover:text-brass"
                    data-testid={`crosspost-toggle-${it.id.slice(0, 8)}`}
                    aria-label={isExcluded ? "include" : "exclude"}
                  >
                    {isExcluded ? <Square className="w-5 h-5" /> : <CheckSquare className="w-5 h-5 text-brass" />}
                  </button>
                  <div className="w-14 h-14 bg-linen border border-taupe/30 overflow-hidden flex-shrink-0">
                    {it.photos?.[0] ? <img src={fileUrl(it.photos[0])} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/admin/inventory/${it.id}`} className="font-serif text-base lowercase text-charcoal hover:text-brass truncate block">
                      {it.title}
                    </Link>
                    <p className="text-[0.7rem] text-taupe tracking-widest uppercase mt-0.5">
                      {it.brand || "—"} · {it.stage || "—"} · {it.photos?.length || 0} photo{it.photos?.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-lg text-brass tabular-nums">{formatMoney(it.sale_price)}</p>
                    <p className="text-[0.6rem] text-taupe uppercase tracking-widest">sale price</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {lastRun && lastRun.results && lastRun.results.length > 0 && (
        <div className="mt-5 border border-taupe/40 bg-linen/40 p-4" data-testid="cross-post-results">
          <p className="editorial-label mb-3">
            last run · {lastRun.successful} succeeded · {lastRun.failed} failed
          </p>
          <ul className="text-xs space-y-1.5">
            {lastRun.results.map((r) => (
              <li key={r.item_id} className="flex items-start gap-3">
                <span className={`font-mono w-3 ${r.status === "success" ? "text-emerald-700" : "text-red-700"}`}>
                  {r.status === "success" ? "✓" : "✗"}
                </span>
                <span className="font-mono text-charcoal w-16">{r.sku || "—"}</span>
                <span className="flex-1 truncate text-espresso">{r.title || "(untitled)"}</span>
                {r.status === "success" && r.listing_url ? (
                  <a href={r.listing_url} target="_blank" rel="noreferrer" className="text-brass underline flex items-center gap-1">
                    live <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-red-700 max-w-md truncate" title={r.error}>{r.error || "failed"}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ScheduledBatchesPanel />
    </section>
  );
}

// ---------------- Scheduled batches (upcoming + history) ----------------
function ScheduledBatchesPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/scheduled-batches?limit=20");
      setEntries(data.entries || []);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("scheduled-batches load failed", e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancel = async (batchId) => {
    if (!window.confirm("Cancel this pending scheduled batch? It won't run.")) return;
    try {
      await api.delete(`/inventory/scheduled-batches/${batchId}`);
      toast.success("batch cancelled.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not cancel.");
    }
  };

  const fmtLocal = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  };

  if (loading) return null;
  if (!entries.length) return null;

  return (
    <div className="mt-8 border-t border-taupe/40 pt-5" data-testid="scheduled-batches-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="editorial-label flex items-center gap-2">
          <Clock className="w-3 h-3" /> scheduled batches
        </span>
        <button
          onClick={load}
          className="text-[10px] uppercase tracking-widest text-espresso hover:text-brass"
          data-testid="scheduled-batches-refresh"
        >
          refresh
        </button>
      </div>
      <div className="bg-ivory border border-taupe/40 divide-y divide-taupe/30" data-testid="scheduled-batches-list">
        {entries.map((b) => {
          const statusColor = {
            pending: "text-amber-800",
            running: "text-blue-700",
            completed: "text-emerald-700",
            cancelled: "text-taupe",
            failed: "text-red-700",
          }[b.status] || "text-espresso";
          const itemCount = (b.item_ids || []).length;
          return (
            <div key={b.id} className="px-3 py-2 flex items-center gap-3 text-xs" data-testid={`scheduled-batch-${b.id.slice(0, 8)}`}>
              <span className={`font-mono w-20 uppercase tracking-widest text-[10px] ${statusColor}`}>{b.status}</span>
              <span className="font-mono text-espresso w-32">{fmtLocal(b.run_at)}</span>
              <span className="text-charcoal w-20">{itemCount} item{itemCount === 1 ? "" : "s"}</span>
              <span className="flex-1 truncate text-espresso/70">
                {b.preset ? `preset · ${b.preset.replace(/_/g, " ")}` : "custom time"}
                {b.repeat_weekly ? " · 🔁 weekly" : ""}
                {b.summary ? ` · ${b.summary.successful}✓ ${b.summary.failed}✗` : ""}
                {b.note ? ` · ${b.note}` : ""}
              </span>
              {b.status === "pending" && (
                <button
                  onClick={() => cancel(b.id)}
                  className="text-red-700 hover:bg-red-700 hover:text-ivory p-1 -m-1"
                  data-testid={`cancel-batch-${b.id.slice(0, 8)}`}
                  aria-label="cancel"
                  title="cancel this scheduled batch"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
